import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import * as Api from "@/api";
import Search from "@/design/components/Search";
import FormGroup from "@/design/forms/FormGroup";
import useSearch from "@/utils/hooks/useSearch";
import Valkyrie, { viPlus, viXmark } from "@sippy-platform/valkyrie";

import {
  Alert,
  Box,
  Button,
  Drawer,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";

import AddDistinctiveRingtone from "./AddDistinctiveRigntone";
import DistinctiveRingtoneItem from "./DistinctiveRingtoneItem";

function DistinctiveRingtones({ user, dispatch }) {
  useEffect(() => {
    Promise.resolve(Api.getDistinctiveRingtones());
  }, []);

  const distinctiveRingtonesStore = useSelector((state) => state.domain.distinctiveRingtones);

  // Ringtone drawer
  const [drawer, setDrawer] = useState(null);
  const [searchValue, setSearchValue] = useState("");

  const searchResults = useSearch(distinctiveRingtonesStore.list, searchValue, ["name"]);

  const closeDrawer = useCallback(() => {
    setDrawer(undefined);
    setSearchValue("");
  }, []);

  return (
    <FormGroup
      title="Available distinctive ringtones"
      actions={
        <Button variant="contained" onClick={() => setDrawer(true)} startIcon={<Valkyrie icon={viPlus} />}>
          Add
        </Button>
      }
    >
      <Grid size={12}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(min(16rem, 100%), 1fr))",
            gap: 1,
          }}
        >
          {user.distinctiveRingtones.length ? (
            user.distinctiveRingtones.map((distinctiveRingtone, key) => (
              <DistinctiveRingtoneItem
                key={key}
                distinctiveRingtone={distinctiveRingtone}
                dispatch={dispatch}
                user={user}
              />
            ))
          ) : (
            <Typography sx={{ gridColumn: "1 / -1" }}>This user has no distinctive ringtones enabled.</Typography>
          )}
        </Box>
      </Grid>
      <Drawer
        open={!!drawer}
        onClose={() => closeDrawer()}
        anchor="right"
        slotProps={{
          paper: {
            sx: (theme) => ({ maxWidth: 320, width: 1, p: 1.5, display: "flex", gap: 1, ...theme.scrollbar }),
            elevation: 4,
          },
        }}
      >
        <Stack
          direction="row"
          sx={{
            gap: 1,
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h5">Add distinctive ringtone</Typography>
          <IconButton onClick={() => closeDrawer()} color="inherit">
            <Valkyrie icon={viXmark} />
          </IconButton>
        </Stack>
        <Box sx={{ position: "sticky", top: 0, zIndex: 1000 }}>
          <Search blur value={searchValue} onChange={setSearchValue} />
        </Box>
        {user.distinctiveRingtones.length >= 6 && (
          <Alert severity="info">You have reached the limit of 6 distinctive ringtones.</Alert>
        )}
        <List>
          {searchResults.length >= 1 ? (
            searchResults.map((distinctiveRingtone) => (
              <AddDistinctiveRingtone
                distinctiveRingtone={distinctiveRingtone}
                user={user}
                dispatch={dispatch}
                key={distinctiveRingtone.id}
              />
            ))
          ) : (
            <ListItem>
              <ListItemText primary="There are no distinctive ringtones available..." />
            </ListItem>
          )}
        </List>
      </Drawer>
    </FormGroup>
  );
}

DistinctiveRingtones.propTypes = {
  user: PropTypes.object,
  dispatch: PropTypes.func,
};

export default DistinctiveRingtones;
